import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import type {
  MyBrandKpiCards as MyBrandKpiCardsData,
  MyBrandKpiCharts as MyBrandKpiChartsData,
  CompetitorKpiCards as CompetitorKpiCardsData,
  CompetitorKpiCharts as CompetitorKpiChartsData,
  CompetitorAspectRatioMix,
  CompetitorKpiMetric,
} from "@nexus/contracts";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  competitorOptionsQueryOptions,
  industriesQueryOptions,
} from "@/lib/queries/opportunity-sets.queries";
import {
  dashboardSummaryQueryOptions,
  dashboardMyBrandChartQueryOptions,
  dashboardCompetitorSummaryQueryOptions,
  dashboardCompetitorChartQueryOptions,
  dashboardCompetitorAspectRatioQueryOptions,
  type MyBrandDashboardFilters,
  type CompetitorDashboardFilters,
} from "@/lib/queries/dashboard.queries";
import { resolveDashboardRequestWindow } from "../../../lib/resolve-dashboard-request-window";
import { findBrandImageUrl } from "../../../lib/competitor-filter-defaults";
import { useDashboardFiltersStore } from "../../../stores/dashboardFilters.store";
import { useDashboardAsyncResource } from "../../../hooks/useDashboardAsyncResource";
import {
  MyBrandKpiCards,
  type MyBrandKpiCardsState,
} from "../../MyBrandKpiCards/MyBrandKpiCards";
import { MyBrandKpiChart } from "../../MyBrandKpiChart/MyBrandKpiChart";
import {
  CompetitorKpiCards,
  type CompetitorKpiCardsState,
} from "../../CompetitorKpiCards/CompetitorKpiCards";
import { KpiInsightLine } from "../../KpiInsightLine/KpiInsightLine";
import { CompetitorKpiChart } from "../../CompetitorKpiChart/CompetitorKpiChart";
import { CompetitorAspectRatioChart } from "../../CompetitorAspectRatioChart/CompetitorAspectRatioChart";
import { CompetitorCreativeRow } from "../../CreativeRow/CompetitorCreativeRow";
import { COMPETITOR_CREATIVE_ROWS } from "../../../lib/competitor-creative-rows";
import { DashboardOpportunitySetsSection } from "../../DashboardOpportunitySetsSection/DashboardOpportunitySetsSection";
import { deriveCardState } from "../../../lib/card-state";
import { buildCompetitorInsight } from "../../../lib/competitor-insight";
import {
  resolveCompetitorSelectionState,
  toCompetitorSectionState,
} from "../../../lib/competitor-selection-state";
import { buildMyBrandCardViews } from "../../../lib/metrics";
import { applyCardOrder, resolveVisibleSelection } from "../../../lib/card-prefs";
import { useKpiSelectorCatalog } from "@/features/kpi-settings/hooks/useKpiSelectorCatalog";
import { useDashboardCardLayout } from "../../../hooks/useDashboardCardLayout";

const DEFAULT_COMPETITOR_METRIC: CompetitorKpiMetric = "activeAds";

export function DashboardPage() {
  const { t } = useTranslation();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  // Drive the KPI request off the *applied* snapshot — the header edits a draft
  // that only commits here once the user clicks Apply.
  const {
    mode,
    appliedCompetitorFilters,
    appliedMyBrandFilters,
    selectedCards: selectedCardsArr,
    northStar,
    toggleCard,
    setDashboardDateReturn,
    isCompetitorSeedSettledIncomplete,
  } = useDashboardFiltersStore();

  // Arriving here (top nav or Back) consumes the date a creative row saved on its way out.
  useEffect(() => {
    setDashboardDateReturn(null);
  }, [setDashboardDateReturn]);

  const { metricById } = useKpiSelectorCatalog("overview");
  const cardLayout = useDashboardCardLayout(activeTenantId);
  const disabledCardsArr = useMemo(
    () => [...cardLayout.disabledSet],
    [cardLayout.disabledSet],
  );
  const visibleSelectedCards = useMemo(
    () => resolveVisibleSelection(selectedCardsArr, disabledCardsArr),
    [selectedCardsArr, disabledCardsArr],
  );

  const { data: industries = [], isLoading: industriesLoading } = useQuery(
    industriesQueryOptions("dashboard", activeTenantId),
  );

  const competitorOptionsTenantId = mode === "competitors" ? activeTenantId : null;
  const { data: myIndustryBrands = [] } = useQuery(
    competitorOptionsQueryOptions(
      "dashboard",
      competitorOptionsTenantId,
      appliedCompetitorFilters.myIndustry,
    ),
  );
  const { data: competitorBrands = [] } = useQuery(
    competitorOptionsQueryOptions(
      "dashboard",
      competitorOptionsTenantId,
      appliedCompetitorFilters.competitorsIndustry,
      appliedCompetitorFilters.platforms,
    ),
  );
  const youLogoUrl = useMemo(
    () => findBrandImageUrl(myIndustryBrands, appliedCompetitorFilters.myBrand),
    [myIndustryBrands, appliedCompetitorFilters.myBrand],
  );
  const competitorLogoUrl = useMemo(
    () => findBrandImageUrl(competitorBrands, appliedCompetitorFilters.mainCompetitor),
    [competitorBrands, appliedCompetitorFilters.mainCompetitor],
  );

  const myBrandWindow = resolveDashboardRequestWindow(appliedMyBrandFilters);
  const competitorWindow = resolveDashboardRequestWindow(appliedCompetitorFilters);

  // Normalize the store's My Brand filters for the API contract (arrays only):
  //   platforms null (All) → []  — param omitted by query builder, backend returns all rows
  //   products  null (All) → []  — param omitted, backend returns all rows
  //   products  []   (none) → ['__no_match__'] — param present but never matches, backend returns 0 rows
  //   [product_code values] → pass through
  const myBrandFiltersTyped: MyBrandDashboardFilters = {
    platforms: appliedMyBrandFilters.platforms ?? [],
    accounts: appliedMyBrandFilters.accounts ?? [],
    products:
      appliedMyBrandFilters.products === null
        ? []
        : appliedMyBrandFilters.products.length === 0
          ? ["__no_match__"]
          : appliedMyBrandFilters.products,
    dateRange: appliedMyBrandFilters.dateRange as MyBrandDashboardFilters["dateRange"],
    startDate: myBrandWindow.startDate,
    endDate: myBrandWindow.endDate,
    activePresetId: appliedMyBrandFilters.activePresetId,
  };
  const competitorFiltersTyped: CompetitorDashboardFilters = {
    myIndustry: appliedCompetitorFilters.myIndustry,
    myBrand: appliedCompetitorFilters.myBrand,
    industryProducts: appliedCompetitorFilters.industryProducts,
    competitorsIndustry: appliedCompetitorFilters.competitorsIndustry,
    mainCompetitor: appliedCompetitorFilters.mainCompetitor,
    benchmark: appliedCompetitorFilters.benchmark,
    platforms: appliedCompetitorFilters.platforms,
    dateRange: appliedCompetitorFilters.dateRange as CompetitorDashboardFilters["dateRange"],
    startDate: competitorWindow.startDate,
    endDate: competitorWindow.endDate,
    activePresetId: appliedCompetitorFilters.activePresetId,
  };

  const filtersHash =
    mode === "my-brand"
      ? JSON.stringify(myBrandFiltersTyped)
      : JSON.stringify(competitorFiltersTyped);
  const resetKey = `${activeTenantId ?? ""}:${mode}:${filtersHash}`;

  // Cards and chart load as two independent async jobs per mode so the cards can
  // render before the (heavier) chart data arrives.
  const myBrandSummaryOptions = dashboardSummaryQueryOptions(
    mode === "my-brand" ? activeTenantId : null,
    myBrandFiltersTyped,
  );
  const myBrandChartOptions = dashboardMyBrandChartQueryOptions(
    mode === "my-brand" ? activeTenantId : null,
    myBrandFiltersTyped,
  );
  // Without a brand and competitor the worker can only answer null, so don't ask.
  const competitorSelectionState = resolveCompetitorSelectionState(
    appliedCompetitorFilters,
    isCompetitorSeedSettledIncomplete,
  );
  const competitorTenantId =
    mode === "competitors" && competitorSelectionState === "complete" ? activeTenantId : null;
  const competitorSummaryOptions = dashboardCompetitorSummaryQueryOptions(
    competitorTenantId,
    competitorFiltersTyped,
  );
  const competitorChartOptions = dashboardCompetitorChartQueryOptions(
    competitorTenantId,
    competitorFiltersTyped,
  );
  const competitorAspectRatioOptions = dashboardCompetitorAspectRatioQueryOptions(
    competitorTenantId,
    competitorFiltersTyped,
  );

  const { data: myBrandSummary } = useQuery(myBrandSummaryOptions);
  const { data: myBrandChart } = useQuery(myBrandChartOptions);
  const { data: competitorSummary } = useQuery(competitorSummaryOptions);
  const { data: competitorChart } = useQuery(competitorChartOptions);
  const { data: competitorAspectRatio } = useQuery(competitorAspectRatioOptions);

  const myBrandCardsResource = useDashboardAsyncResource<MyBrandKpiCardsData>({
    summary: myBrandSummary,
    targetKey: myBrandSummaryOptions.queryKey,
    resultField: "kpiCards",
    resetKey,
    enabled: mode === "my-brand" && !!activeTenantId,
  });

  const myBrandChartResource = useDashboardAsyncResource<MyBrandKpiChartsData>({
    summary: myBrandChart,
    targetKey: myBrandChartOptions.queryKey,
    resultField: "charts",
    resetKey,
    enabled: mode === "my-brand" && !!activeTenantId,
  });

  const competitorResource = useDashboardAsyncResource<CompetitorKpiCardsData>({
    summary: competitorSummary,
    targetKey: competitorSummaryOptions.queryKey,
    resultField: "kpiCards",
    resetKey,
    enabled: !!competitorTenantId,
  });

  const competitorChartResource = useDashboardAsyncResource<CompetitorKpiChartsData>({
    summary: competitorChart,
    targetKey: competitorChartOptions.queryKey,
    resultField: "charts",
    resetKey,
    enabled: !!competitorTenantId,
  });

  const competitorAspectRatioResource = useDashboardAsyncResource<CompetitorAspectRatioMix>({
    summary: competitorAspectRatio,
    targetKey: competitorAspectRatioOptions.queryKey,
    resultField: "aspectRatio",
    resetKey,
    enabled: !!competitorTenantId,
  });

  const myBrandKpiCards = myBrandCardsResource.data;
  const myBrandCharts = myBrandChartResource.data;
  const competitorKpiCards = competitorResource.data;

  const myBrandCardState = deriveCardState(
    myBrandCardsResource.timedOut,
    myBrandCardsResource.status,
    myBrandKpiCards,
  ) as MyBrandKpiCardsState;

  const myBrandChartState = deriveCardState(
    myBrandChartResource.timedOut,
    myBrandChartResource.status,
    myBrandCharts,
  ) as MyBrandKpiCardsState;

  const competitorCardState: CompetitorKpiCardsState = toCompetitorSectionState(
    competitorSelectionState,
    deriveCardState(competitorResource.timedOut, competitorResource.status, competitorKpiCards),
  );

  const competitorChartState: CompetitorKpiCardsState = toCompetitorSectionState(
    competitorSelectionState,
    deriveCardState(
      competitorChartResource.timedOut,
      competitorChartResource.status,
      competitorChartResource.data,
    ),
  );

  const competitorAspectRatioState: CompetitorKpiCardsState = toCompetitorSectionState(
    competitorSelectionState,
    deriveCardState(
      competitorAspectRatioResource.timedOut,
      competitorAspectRatioResource.status,
      competitorAspectRatioResource.data,
    ),
  );

  const [selectedCompetitorMetric, setSelectedCompetitorMetric] =
    useState<CompetitorKpiMetric>(DEFAULT_COMPETITOR_METRIC);
  const [hoveredCompetitorMetric, setHoveredCompetitorMetric] =
    useState<CompetitorKpiMetric | null>(null);

  // A card swapped for its loading skeleton never fires mouseleave.
  useEffect(() => {
    setHoveredCompetitorMetric(null);
  }, [resetKey]);

  const insightCard =
    competitorCardState === "ready"
      ? competitorKpiCards?.[hoveredCompetitorMetric ?? selectedCompetitorMetric]
      : undefined;
  const competitorInsight = useMemo(
    () =>
      insightCard && appliedCompetitorFilters.mainCompetitor
        ? buildCompetitorInsight(t, insightCard)
        : null,
    [t, insightCard, appliedCompetitorFilters.mainCompetitor],
  );

  const myBrandCardViews = useMemo(
    () =>
      myBrandKpiCards
        ? buildMyBrandCardViews(myBrandKpiCards, northStar, metricById)
        : undefined,
    [myBrandKpiCards, northStar, metricById],
  );
  const orderedCardViews = useMemo(
    () => (myBrandCardViews ? applyCardOrder(myBrandCardViews, cardLayout.order) : undefined),
    [myBrandCardViews, cardLayout.order],
  );

  const industryLabelByIndex = useMemo(() => {
    const map = new Map<string, string>();
    for (const industry of industries) {
      map.set(industry.index, industry.title);
    }
    return map;
  }, [industries]);

  if (!industriesLoading && industries.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/40">
          <AlertTriangle className="h-7 w-7 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-foreground">
            No industries found
          </h2>
          <p className="text-sm text-muted-foreground">
            Please contact administration to set up industry data for your
            account.
          </p>
        </div>
      </div>
    );
  }

  const myBrandCardsDisplayState: MyBrandKpiCardsState =
    cardLayout.isLoading && myBrandCardState === "ready" ? "loading" : myBrandCardState;
  const myBrandChartDisplayState: MyBrandKpiCardsState =
    cardLayout.isLoading && myBrandChartState === "ready" ? "loading" : myBrandChartState;

  if (mode === "my-brand") {
    return (
      <div className="flex flex-1 flex-col gap-6 p-6">
        <MyBrandKpiCards
          state={myBrandCardsDisplayState}
          cards={orderedCardViews}
          activePresetId={appliedMyBrandFilters.activePresetId}
          selectedCards={visibleSelectedCards}
          disabledCards={cardLayout.disabledSet}
          onCardToggle={toggleCard}
          onCardDisabledToggle={cardLayout.canEdit ? cardLayout.toggleDisabled : undefined}
          onCardOrderChange={cardLayout.canEdit ? cardLayout.setOrder : undefined}
          isOrderCustomized={cardLayout.canEdit && cardLayout.isOrderCustomized}
          onCardOrderReset={cardLayout.canEdit ? cardLayout.resetLayout : undefined}
        />
        <MyBrandKpiChart
          state={myBrandChartDisplayState}
          charts={myBrandCharts ?? undefined}
          northStar={northStar}
          selectedCards={visibleSelectedCards}
          cardOrder={cardLayout.order}
          window={myBrandWindow}
          catalogById={metricById}
        />
        <DashboardOpportunitySetsSection
          tenantId={activeTenantId}
          industryLabelByIndex={industryLabelByIndex}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-6">
      <CompetitorKpiCards
        state={competitorCardState}
        data={competitorKpiCards ?? undefined}
        animationKey={filtersHash}
        selectedMetric={selectedCompetitorMetric}
        onCardClick={setSelectedCompetitorMetric}
        onCardHover={setHoveredCompetitorMetric}
        youLogoUrl={youLogoUrl}
        competitorLogoUrl={competitorLogoUrl}
      />
      {insightCard && competitorInsight && (
        <KpiInsightLine
          kpiLabel={insightCard.label}
          verdict={competitorInsight.verdict}
          sentence={competitorInsight.sentence}
        />
      )}
      <CompetitorKpiChart
        state={competitorChartState}
        charts={competitorChartResource.data ?? undefined}
        metric={selectedCompetitorMetric}
        metricLabel={competitorKpiCards?.[selectedCompetitorMetric]?.label ?? ""}
        window={competitorWindow}
        youLogoUrl={youLogoUrl}
        competitorLogoUrl={competitorLogoUrl}
      />
      {COMPETITOR_CREATIVE_ROWS.map((row) => (
        <CompetitorCreativeRow
          key={row.id}
          row={row}
          tenantId={competitorTenantId}
          filters={competitorFiltersTyped}
          headerWindow={competitorWindow}
          appliedCompetitor={appliedCompetitorFilters}
          appliedMyBrand={appliedMyBrandFilters}
          resetKey={resetKey}
          isBlocked={competitorSelectionState === "blocked"}
        />
      ))}
      <CompetitorAspectRatioChart
        state={competitorAspectRatioState}
        mix={competitorAspectRatioResource.data ?? undefined}
        window={competitorWindow}
      />
    </div>
  );
}
